import React, { useState, useEffect } from 'react';
import { X, Save, Package, Barcode, Settings, AlertCircle } from 'lucide-react';
import type { ProductoCliente, ProductoClienteCreate } from '../types/produtoCliente';
import produtoClienteService from '../services/produtoClienteService';
import { msg } from '../utils/messages';

interface Props {
  clienteId: number;
  producto?: ProductoCliente | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormErrors {
  gtin_principal?: string;
}

const ProdutoClienteFormModal: React.FC<Props> = ({ clienteId, producto, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<ProductoClienteCreate>({
    codigo: '',
    descricao_completa: '',
    unidade_medida: 'UN',
    produto_ativo: true,
    rms_cancelado: false,
    serializado_sn: false,
    controlar_validade: false,
    gtin_principal: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (producto) {
      setFormData({
        codigo: producto.codigo,
        referencia: producto.referencia,
        descricao_completa: producto.descricao_completa,
        // Campos removidos: descricao, ean
        gtin_principal: producto.gtin_principal || '',
        rms: producto.rms,
        rms_cancelado: producto.rms_cancelado || false,
        codigo_alternativo_no_fornecedor: producto.codigo_alternativo_no_fornecedor,
        unidade_medida: producto.unidade_medida,
        grupo: producto.grupo,
        serializado_sn: producto.serializado_sn,
        controlar_validade: producto.controlar_validade,
        produto_ativo: producto.produto_ativo,
      });
    }
  }, [producto]);

  // Story 2.33: validar GTIN APENAS QUANDO PREENCHIDO (vazio é aceitavel).
  // Mantemos validacao de formato (13/14 digitos numericos) para evitar lixo.
  const validateGTIN = (gtin: string): string | undefined => {
    if (!gtin || !gtin.trim()) {
      return undefined; // Story 2.33: GTIN vazio agora e permitido
    }
    const cleanGTIN = gtin.trim();
    if (!/^\d+$/.test(cleanGTIN)) {
      return 'GTIN deve conter apenas dígitos numéricos';
    }
    if (cleanGTIN.length !== 13 && cleanGTIN.length !== 14) {
      return 'GTIN deve ter 13 ou 14 dígitos';
    }
    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Story 2.33: validacao de FORMATO de GTIN (se preenchido) — vazio aceitavel.
    // Antes bloqueava criacao se vazio; agora alinhado com Story 2.30 (Excel sem GTIN).
    const gtinError = validateGTIN(formData.gtin_principal || '');
    if (gtinError) {
      setErrors({ gtin_principal: gtinError });
      msg.error(gtinError);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      if (producto) {
        await produtoClienteService.atualizar(clienteId, producto.id, formData);
      } else {
        await produtoClienteService.criar(clienteId, formData);
      }
      msg.success(producto ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!');
      onSuccess();
    } catch (error: any) {
      console.error('Erro ao salvar produto:', error);
      // Tratar erro de validação do backend
      if (error.response?.data?.gtin_principal) {
        const backendError = Array.isArray(error.response.data.gtin_principal)
          ? error.response.data.gtin_principal[0]
          : error.response.data.gtin_principal;
        setErrors({ gtin_principal: backendError });
        msg.error(backendError);
      } else if (error.response?.data?.detail) {
        // Mensagem util do backend (ex.: codigo pertence a produto EXCLUIDO —
        // orienta a usar "Mostrar excluidos" e Restaurar). Antes era engolida
        // pelo generico abaixo, escondendo a solucao do usuario.
        msg.error(error.response.data.detail);
      } else {
        msg.error('Erro ao salvar produto');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {producto ? 'Editar Produto' : 'Novo Produto'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {/* Descrições no topo - largura total */}
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descrição Completa *
              </label>
              <input
                type="text"
                value={formData.descricao_completa}
                onChange={(e) => setFormData({ ...formData, descricao_completa: e.target.value })}
                required
                placeholder="Nome completo do produto"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Campo Descrição Curta removido - usar apenas descricao_completa */}
          </div>

          {/* Grid de 3 colunas */}
          <div className="grid grid-cols-3 gap-4">
            {/* Coluna 1 - Identificação */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 pb-1.5 border-b border-blue-200 dark:border-blue-800">
                <Package className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                  Identificação
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  required
                  placeholder="SKU"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {!!producto && (
                  <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                    ⚠️ Alterar o código pode quebrar referências em contagens existentes. Use com cuidado.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Referência
                </label>
                <input
                  type="text"
                  value={formData.referencia || ''}
                  onChange={(e) => setFormData({ ...formData, referencia: e.target.value })}
                  placeholder="Ref. interna"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Coluna 2 - Códigos */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 pb-1.5 border-b border-emerald-200 dark:border-emerald-800">
                <Barcode className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                  Códigos
                </span>
              </div>

              {/* Campo EAN removido - usar GTIN Principal */}

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  GTIN Principal
                </label>
                <input
                  type="text"
                  value={formData.gtin_principal || ''}
                  onChange={(e) => {
                    setFormData({ ...formData, gtin_principal: e.target.value });
                    if (errors.gtin_principal) setErrors({});
                  }}
                  placeholder="13 ou 14 dígitos (opcional)"
                  className={`w-full px-2.5 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.gtin_principal
                      ? 'border-red-500 dark:border-red-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.gtin_principal && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.gtin_principal}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RMS
                </label>
                <input
                  type="text"
                  value={formData.rms || ''}
                  onChange={(e) => setFormData({ ...formData, rms: e.target.value })}
                  placeholder="Registro ANVISA"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cód. Alt. Fornecedor
                </label>
                <input
                  type="text"
                  value={formData.codigo_alternativo_no_fornecedor || ''}
                  onChange={(e) => setFormData({ ...formData, codigo_alternativo_no_fornecedor: e.target.value })}
                  placeholder="Código externo"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Coluna 3 - Controle */}
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 pb-1.5 border-b border-purple-200 dark:border-purple-800">
                <Settings className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                  Controle
                </span>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Grupo
                </label>
                <input
                  type="text"
                  value={formData.grupo || ''}
                  onChange={(e) => setFormData({ ...formData, grupo: e.target.value })}
                  placeholder="Categoria"
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Unidade Medida *
                </label>
                <select
                  value={formData.unidade_medida}
                  onChange={(e) => setFormData({ ...formData, unidade_medida: e.target.value })}
                  required
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="UN">UN</option>
                  <option value="CX">CX</option>
                  <option value="KG">KG</option>
                  <option value="LT">LT</option>
                </select>
              </div>

              {/* Checkboxes em grid 2x2 */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.produto_ativo}
                    onChange={(e) => setFormData({ ...formData, produto_ativo: e.target.checked })}
                    className="w-3.5 h-3.5 mr-1.5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Ativo</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.serializado_sn}
                    onChange={(e) => setFormData({ ...formData, serializado_sn: e.target.checked })}
                    className="w-3.5 h-3.5 mr-1.5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Serial</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.controlar_validade}
                    onChange={(e) => setFormData({ ...formData, controlar_validade: e.target.checked })}
                    className="w-3.5 h-3.5 mr-1.5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">Validade</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.rms_cancelado}
                    onChange={(e) => setFormData({ ...formData, rms_cancelado: e.target.checked })}
                    className="w-3.5 h-3.5 mr-1.5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700 dark:text-gray-300">RMS Canc.</span>
                </label>
              </div>
            </div>
          </div>

          {/* Footer com botões */}
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 flex items-center gap-1.5"
            >
              <Save className="w-4 h-4" /> {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProdutoClienteFormModal;
